/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Section, Header } from 'components';
import { useParams } from 'react-router-dom';
import Content from 'containers/common/Content';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ToastMessage, NewLocation, ThresholdSettings, AlarmSettings, ParentSelector, AutoShutoffSettings } from 'containers';
import { LocationList } from 'components/LocationList';
import { ListView, LocationFilterState, LocationFacetsData } from 'model/state';
import { Location, LocationType, LocationFilters, User, SystemMode, LocationFacet } from 'model';
import { LocationActions } from './LocationActions';
import ConfirmationDialog from 'containers/common/ConfirmationDialog';
import { LocationHead } from './LocationHead';
import { useModalState, useConfirmationPopupFunction } from 'effects';
import styles from './LocationView.module.css';
import { useLocationService } from './effect/useLocationService';
import { FetchLocationsFn } from '../../containers/Locations';
import AddBulkUnits from 'containers/Locations/AddBulkUnits';
import { analytics } from 'utils/Analytics';

type LocationViewProps = {
  fetchLocations: FetchLocationsFn,
  locationList:     ListView<Location>,
  userId?: string | null,
  isSelectedAll: boolean,
  setSelectAll: (selectAll: boolean) => void,
  setSelectRow: (selectAll: boolean, locationId: string) => void,
  clearMessage: () => void,
  fetchCountryList: () => void,
  cleanRegionList: () => void,
  cleanTimezoneList: () => void,
  isSelectedAny: boolean,
  selectedNumber: number,
  hasError: boolean,
  isSubmitting: boolean,
  addToParent: (parentId: string, parentType: LocationType) => void,
  openConfirmationDialog: (metadata?: { [key: string]: any }) => void,
  removeLocations: () => void,
  parent: Location | null,
  updateSearch: (search: string) => void,
  search: string,
  selectedLocations: Location[],
  isSubmittingThresholdSettings: boolean,
  filterData: LocationFacetsData,
  filters: LocationFilterState,
  updateLocationFilters: (filters: LocationFilters) => void,
  setCurrentLocation: (location: Location | null) => void,
  isSubmittingAlertSettings: boolean,
  setLocationSystemModeBulk: (systemModeTarget: SystemMode, revertMinutes?: number) => void,
  loggedUser?: User,
  areAllSelectedFullAccessLocations: boolean,
  incrementPage: () => void,
  reinitPageCount: () => void,
  fetchLocationFacets: (facet: LocationFacet) => void,
  setShowLocationFilters: (show: boolean) => void,
  showFilters: boolean,
  setSingleSelectLocation: (location: Location) => void,
  removeSingleSelectLocation: (location: Location) => void,
  singleSelectLocation: Location | null,
  apiCacheDisabled?: boolean,
}

export const LocationView: React.FC<LocationViewProps> = React.memo(({
  fetchLocations,
  locationList,
  userId,
  isSelectedAll,
  setSelectAll,
  setSelectRow,
  clearMessage,
  fetchCountryList,
  cleanRegionList,
  isSelectedAny,
  selectedNumber,
  cleanTimezoneList,
  hasError,
  isSubmitting,
  addToParent,
  openConfirmationDialog,
  removeLocations,
  parent,
  search,
  updateSearch,
  selectedLocations,
  isSubmittingThresholdSettings,
  filterData,
  filters,
  updateLocationFilters,
  setCurrentLocation,
  isSubmittingAlertSettings,
  setLocationSystemModeBulk,
  loggedUser,
  areAllSelectedFullAccessLocations,
  incrementPage,
  reinitPageCount,
  fetchLocationFacets,
  setShowLocationFilters,
  showFilters,
  setSingleSelectLocation,
  removeSingleSelectLocation,
  singleSelectLocation,
  apiCacheDisabled,
}) => {
  const { t } = useTranslation();
  let { parentId } = useParams();

  const [showNewLocation, setShowNewLocation] = useState(false);
  const [showBulkAddUnits, setShowBulkAddUnits] = useState(false);
  const [locationType, setLocationType] = useState(LocationType.UNIT);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [showThresholdsModal, setShowThresholdsModal] = useModalState(isSubmittingThresholdSettings, hasError);
  const [showAutoShutoffModal, setShowAutoShutoffModal] = useModalState(isSubmittingThresholdSettings, hasError);
  const [showAlarmSettingsModal, setShowAlarmSettingsModal] = useModalState(isSubmittingAlertSettings, hasError);
  const [confirmationData, handleOpenConfirmationPopup] = useConfirmationPopupFunction(openConfirmationDialog);

  useEffect(() => { fetchCountryList(); }, []);

  useLocationService(userId, parentId, search, filters, locationList.page, isSubmitting, hasError, fetchLocations, reinitPageCount, updateSearch, updateLocationFilters, apiCacheDisabled, () => {
    setShowNewLocation(false);
    setShowBulkAddUnits(false);
  })

  useEffect(() => {
    return function cleanup() {
      reinitPageCount();
      updateSearch('');
      setShowLocationFilters(false);
      updateLocationFilters({
        type: null,
        cities:[],
        states: [],
        countries: [],
        zipCodes: [],
        offlineDevices: false,
      });
    };
  }, []);

  useEffect(() => {
    if (userId) {
      fetchLocationFacets({ userId, search: '' })
    }
  }, [userId]);

  const clearSingleSelectLocation = () => {
    if (singleSelectLocation) {
      removeSingleSelectLocation(singleSelectLocation);
    }
  }

  const handleUpdateSearch = (search: string) => {
    reinitPageCount();
    updateSearch(search);
  };

  const handleNewLocationClose = () => {
    setShowNewLocation(false);
    setCurrentLocation(null);
    clearMessage();
  };

  const handleGroupSelectorClose = () => {
    clearSingleSelectLocation();
    setShowGroupSelector(false);
  }

  const handleAddClick = (type: LocationType) => {
    setLocationType(type);
    setCurrentLocation(null);
    clearMessage();
    cleanRegionList();
    cleanTimezoneList();
    setShowNewLocation(true);
  }

  const handleAddBulkUnitClick = () => {
    setShowBulkAddUnits(true);
  };

  const handleAddBulkUnitsClose = () => {
    setShowBulkAddUnits(false);
    clearMessage();
  };

  const handleEditClick = (location: Location) => {
    cleanRegionList();
    cleanTimezoneList();
    clearMessage();
    setCurrentLocation(location);
    setShowNewLocation(true);
  }

  const handleAddTo = (type: LocationType, location?: Location) => {
    if (location) {
      setSingleSelectLocation(location);
    }

    setLocationType(type);
    setShowGroupSelector(true);
    clearMessage();
  }

  const handleSleepModeAction = (hours: number, location?: Location) => {
    handleOpenConfirmationPopup(
      t(`sleep-${hours}-hours-confirmation-title`),
      t(`sleep-${hours}-hours-confirmation-description`),
      t('sleep-action'),
      (metadata) => {
        if (metadata?.location) {
          setSingleSelectLocation(metadata.location);
        }
        setLocationSystemModeBulk(SystemMode.SLEEP, hours * 60);
       analytics.trackAlertSleepAction(hours, loggedUser?.account?.id || '', location?.id || '')
      },
      () => clearSingleSelectLocation(),
      { location }
    )
  }

  const handleChangeModeAction = (target: SystemMode, location?: Location) => {
    handleOpenConfirmationPopup(
      t(`change-mode-${target === SystemMode.HOME ? 'home' : 'away'}-confirmation-title`),
      t(`change-mode-${target === SystemMode.HOME ? 'home' : 'away'}-confirmation-description`, {
        count: location ? 1 : selectedLocations.length
      }),
      t('change-mode-action'),
      (metadata) => {
        if (metadata?.location) {
          setSingleSelectLocation(metadata.location);
        }
        setLocationSystemModeBulk(target);
      },
      () => clearSingleSelectLocation(),
      { location }
    )
  }

  const handleRemove = (location?: Location) => {
    handleOpenConfirmationPopup(
      t('remove-location-confirmation-title'),
      t('remove-location-confirmation-description'),
      t('remove-location-confirmation-accept-action'),
      (metadata) => {
        if (metadata?.location) {
          setSingleSelectLocation(metadata.location);
        }
        removeLocations();
      },
      () => clearSingleSelectLocation(),
      { location }
    )
  };

  const handleSetThresholdsClick = (location?: Location) => {
    if (location) {
      setSingleSelectLocation(location);
    }
    setShowThresholdsModal(true);
  }

  const handleSetThresholdsHide = () => {
    setShowThresholdsModal(false);
    clearSingleSelectLocation();
  }

  const handleAutoShutoffClick = (location?: Location) => {
    if (location) {
      setSingleSelectLocation(location);
    }
    setShowAutoShutoffModal(true);
  }

  const handleAutoShutoffHide = () => {
    setShowAutoShutoffModal(false);
    clearSingleSelectLocation();
  }

  const handleAlarmSettingsClick = (location?: Location) => {
    if (location) {
      setSingleSelectLocation(location);
    }
    setShowAlarmSettingsModal(true);
  }

  const handleAlarmSettingsHide = () => {
    setShowAlarmSettingsModal(false);
    clearSingleSelectLocation();
  }

  const handleUpdateLocationFilters = (filters: LocationFilters) => {
    reinitPageCount();
    updateLocationFilters(filters);
  };

  return (
    <Section>
      { !showThresholdsModal && !showAutoShutoffModal && !showAlarmSettingsModal && !showNewLocation && <ToastMessage />}
      <Header className={styles.header}>
        <LocationHead parent={parent} search={search} updateSearch={handleUpdateSearch} />
      </Header>
      <Row>
        <Col xs="12" className="pl-0 pr-0">
          <Row>
            <Col xs="12">
              <LocationActions
                isSelectedAny                     = {isSelectedAny}
                selectedNumber                    = {selectedNumber}
                onAddLocationClick                = {handleAddClick}
                onAddBulkUnitClick                = {handleAddBulkUnitClick}
                onAddToClick                      = {handleAddTo}
                onRemoveClick                     = {handleRemove}
                onSetThresholdsClick              = {() => handleSetThresholdsClick()}
                onAutoShutoffClick                = {() => handleAutoShutoffClick()}
                filterData                        = {filterData}
                filters                           = {filters}
                updateLocationFilters             = {handleUpdateLocationFilters}
                locationsCount                    = {(locationList.rows || []).length}
                onSetAlarmPreferencesClick        = {() => {handleAlarmSettingsClick()}}
                onLocationSleepClick              = {handleSleepModeAction}
                onLocationChangeModeClick         = {handleChangeModeAction}
                loggedUser                        = {loggedUser}
                areAllSelectedFullAccessLocations = {areAllSelectedFullAccessLocations}
                setShowLocationFilters            = {setShowLocationFilters}
                showFilters                       = {showFilters}
                fetchLocationFacets               = {fetchLocationFacets}
                parent                            = {parent}
                isRootLevel                       = {!parentId}
              />
              <div className={styles.divider}></div>
            </Col>
          </Row>
          <Content>
            <Col xs="12">
              <InfiniteScroll
                className       = {styles.infiniteScroll}
                dataLength      = {locationList.rows?.length || 0}
                next            = {incrementPage}
                hasMore         = {(locationList.total || 0) > (locationList.rows?.length || 0)}
                loader          = {<div className={styles.loader}><span className="spinner-border" role="status" aria-hidden="true"></span></div>}
                endMessage      = {
                  <p style={{textAlign: 'center', marginTop: 30}}>
                    <b>{t('infinite-scroll-end-message')}</b>
                  </p>
                }
              >
                <LocationList
                  parentId                   = {parentId}
                  locationList               = {locationList}
                  isSelectedAll              = {isSelectedAll}
                  setSelectAll               = {setSelectAll}
                  setSelectRow               = {setSelectRow}
                  parent                     = {parent}
                  onEdit                     = {handleEditClick}
                  onSetThresholdsClick       = {handleSetThresholdsClick}
                  onAutoShutoffClick          = {handleAutoShutoffClick}
                  onSetAlarmPreferencesClick = {handleAlarmSettingsClick}
                  onAddToClick               = {handleAddTo}
                  onRemoveClick              = {handleRemove}
                  onLocationSleepClick       = {handleSleepModeAction}
                  onLocationChangeModeClick  = {handleChangeModeAction}
                />
              </InfiniteScroll>
              <NewLocation
                show          = {showNewLocation}
                onHide        = {handleNewLocationClose}
                type          = {locationType}
                parent        = {parent}
              />
              <AddBulkUnits
                show = {showBulkAddUnits}
                onHide = {handleAddBulkUnitsClose}
                parent = {parent}
              />
              <ParentSelector
                show      = {showGroupSelector}
                onHide    = {handleGroupSelectorClose}
                title     = {`${t('group-selector-title', { count: singleSelectLocation === null ? selectedNumber : 1 })} ${locationType === LocationType.REGION ? t('Community') : locationType === LocationType.PROPERTY ? t('Property') : t('Building')}`}
                subtitle  = {`${locationType === LocationType.REGION ? t('group-selector-subtitle-regions') : locationType === LocationType.PROPERTY ? t('group-selector-subtitle-properties') : t('group-selector-subtitle-buildings')}`}
                onSelect  = {(value) => addToParent(value, locationType)}
                type      = {locationType}
                parentId  = {parentId}
              />
              <ThresholdSettings
                show        = {showThresholdsModal}
                onHide      = {handleSetThresholdsHide}
                locations   = {singleSelectLocation === null ? selectedLocations : [singleSelectLocation]}
              />
              <AutoShutoffSettings
                show        = {showAutoShutoffModal}
                onHide      = {handleAutoShutoffHide}
                locations   = {singleSelectLocation === null ? selectedLocations : [singleSelectLocation]}
              />
              <AlarmSettings
                show        = {showAlarmSettingsModal}
                onHide      = {handleAlarmSettingsHide}
                locationIds = {singleSelectLocation === null ? selectedLocations.map(loc => loc.id || '') : [singleSelectLocation.id || '']}
              />
              <ConfirmationDialog
                title       = {confirmationData.title}
                description = {confirmationData.description}
                actionText  = {confirmationData.actionText}
                onAccept    = {confirmationData.onAccept}
                onCancel    = {confirmationData.onCancel}
              />
            </Col>
          </Content>
        </Col>
      </Row>
    </Section>
  )
})
